// src/App.tsx
import { useEffect, useState } from 'react';
import { Routes, Route, useLocation, Navigate } from 'react-router-dom';

/* hooks / redux */
import { useCompanyTheme } from './hooks/useCompanyTheme';
import { useDispatch, useSelector } from 'react-redux';
import { getIsLoggedIn } from 'store/auth/auth-selectors';
import { getUserOperation } from 'store/auth/auth-operations';
import { AppDispatch } from 'store/store';

/* route-helpers */
import { ROUTES } from './configs/routeConstants';
import PublicRoute from './components/routes/PublicRoute';
import ProtectedRoute from './components/routes/ProtectedRoute';
import { isMentalHealthEnabled } from './configs/features';

/* layout (постоянные элементы) */
import { Header } from '@components/widgets/header/Header';
import Footer from './components/widgets/footer/Footer';

/* error boundary */
import ErrorBoundary from 'utils/ErrorBoundary';
import { useMetrikaHit } from '@hooks/useMetrikaHit';
import { useGlobalUtm } from '@hooks/useGlobalUtm';
import { TopWindowNavEnforcer } from '@utils/TopWindowNavEnforcer';
import { preloadAllLazyPages } from '@utils/preloadAllLazyPages';
import { DelayedLoader } from '@components/shared/globalLoader/DelayedLoader';
import { SmartSuspense } from '@components/shared/globalLoader/SmartSuspense';
import { SurveyInviteGate } from '@components/global/SurveyInviteModal/SurveyInviteGate';
import { ScrollToAnchor } from '@components/shared/anchor/ScrollToAnchor';
import { lazyRetry } from '@utils/lazyRetry';
import { ToastContainer } from 'react-toastify';
import { Alert, Button } from 'antd';
import { impersonationClosed } from '@store/impersonation/impersonation-slice';
import 'react-toastify/dist/ReactToastify.css';
import './components/shared/toast/ToastNotifications.scss';
// import { useInitYMetrika } from '@hooks/useInitYMetrika';

/* --- lazy pages (см. предыдущий ответ) --- */
const VerifyEmailPage = lazyRetry(() => import('./views/verifyEmailPage/VerifyEmailPage'));
const SignInPage = lazyRetry(() => import('./views/signInPage/SignInPage'));
const SignUpPage = lazyRetry(() => import('./views/signUpPage/SignUpPage'));
const ForgotPasswordPage = lazyRetry(() => import('@views/forgotPassword/ForgotPassword'));
const ResetPasswordPage = lazyRetry(() => import('@views/resetPassword/ResetPasswordPage'));
const FaqPage = lazyRetry(() =>
  import('@views/faqPage/FaqPage').then(module => ({ default: module.FaqPage })),
);
const HomePage = lazyRetry(() => import('@views/homePage/HomePage'));
const DownloadAppPage = lazyRetry(() => import('@views/downloadAppPage/DownloadAppPage'));
const DocumentsPage = lazyRetry(() => import('@views/documents/DocumentsPage'));
const DocumentViewerPage = lazyRetry(() => import('@views/documentViewerPage/DocumentViewerPage'));
const ContactsPage = lazyRetry(() =>
  import('@views/contacts/ContactsPage').then(module => ({ default: module.ContactsPage })),
);
const InfoPage = lazyRetry(() =>
  import('@views/info/InfoPage').then(module => ({ default: module.InfoPage })),
);
const QuestionnairePage = lazyRetry(() => import('./views/questionnairePage/QuestionnairePage'));
const CheckupStartPage = lazyRetry(() => import('./views/checkupStartPage/CheckupStartPage'));

const ProfilePage = lazyRetry(() => import('./views/profilePage/ProfilePage'));

const HealthPage = lazyRetry(() =>
  import('./views/myHealth/HealthPage').then(module => ({
    default: module.HealthPage,
  })),
);
const MentalHealthLayout = lazyRetry(() =>
  import('./views/myHealth/HealthPage').then(module => ({
    default: module.MentalHealthLayout,
  })),
);
const AdminLayout = lazyRetry(() => import('./admin/AdminLayout'));
const AdminGuard = lazyRetry(() =>
  import('./admin/guards').then(module => ({ default: module.AdminGuard })),
);
const ScopeGuard = lazyRetry(() =>
  import('./admin/guards').then(module => ({ default: module.ScopeGuard })),
);
const AdminHome = lazyRetry(() =>
  import('./admin/guards').then(module => ({ default: module.AdminHome })),
);
const LoginPage = lazyRetry(() => import('./admin/pages/LoginPage'));
const UsersPage = lazyRetry(() => import('./admin/pages/UsersPage'));
const AdminsPage = lazyRetry(() => import('./admin/pages/AdminsPage'));
const AuditLogsPage = lazyRetry(() => import('./admin/pages/AuditLogsPage'));
const ImpersonationsPage = lazyRetry(() => import('./admin/pages/ImpersonationsPage'));
const PoliciesPage = lazyRetry(() => import('./admin/pages/PoliciesPage'));
const PolicyDetailPage = lazyRetry(() => import('./admin/pages/PolicyDetailPage'));
const OrdersPage = lazyRetry(() => import('./admin/pages/OrdersPage'));
const OrderDetailPage = lazyRetry(() => import('./admin/pages/OrderDetailPage'));
const RolesPage = lazyRetry(() => import('./admin/pages/RolesPage'));
const ImpersonationEntry = lazyRetry(() => import('./admin/ImpersonationEntry'));
/* ----------------------------------------- */

const App = () => {
  const dispatch = useDispatch<AppDispatch>();
  const isLoggedIn = useSelector(getIsLoggedIn);
  const isImpersonating = useSelector((state: any) => Boolean(state.impersonation.accessToken));
  const location = useLocation();

  const [preloaded, setPreloaded] = useState(false);

  useCompanyTheme();
  useGlobalUtm();
  useMetrikaHit();
  const isDocumentViewerPage = location.pathname === ROUTES.documentViewer;
  const isAdminPage =
    location.pathname.startsWith('/admin') || location.pathname === '/impersonation';
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const isOpenLink = params.get('open') === 'true';

    if (isOpenLink) {
      sessionStorage.setItem('allowFirstSurvey', '1');
      sessionStorage.setItem('allowRenewSurvey', '1');
    }
  }, []);

  useEffect(() => {
    preloadAllLazyPages().finally(() => setPreloaded(true));
  }, []);
  useEffect(() => {
    if (isLoggedIn && !isImpersonating) dispatch(getUserOperation());
  }, [isLoggedIn, isImpersonating]);
  if (!preloaded) {
    return (
      <div style={{ minHeight: '100vh' }}>
        <DelayedLoader />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      {/* ───── постоянный header ───── */}
      {!isDocumentViewerPage && !isAdminPage && <Header />}
      {isImpersonating && (
        <Alert
          banner
          type="warning"
          message="Режим поддержки: вы просматриваете данные пользователя. Изменение данных и пароля недоступно."
          action={
            <Button
              size="small"
              onClick={async () => {
                try {
                  await (await import('@utils/api')).api.post('/auth/impersonations/end');
                } finally {
                  dispatch(impersonationClosed());
                  window.location.assign('/admin/users');
                }
              }}
            >
              Завершить сеанс
            </Button>
          }
        />
      )}
      {!isAdminPage && !isImpersonating && <SurveyInviteGate />}
      {!isAdminPage && <ScrollToAnchor />}
      {!isAdminPage && <TopWindowNavEnforcer scrollToTop />}
      {/* ───── маршрутизация с перехватом ошибок ───── */}
      <main style={{ flex: 1 }}>
        <ErrorBoundary>
          <SmartSuspense>
            <Routes>
              <Route path="/impersonation" element={<ImpersonationEntry />} />
              <Route path="/admin/login" element={<LoginPage />} />
              <Route path="/admin/mfa" element={<LoginPage />} />
              <Route element={<AdminGuard />}>
                <Route path="/admin" element={<AdminLayout />}>
                  <Route index element={<AdminHome />} />
                  <Route
                    path="users"
                    element={
                      <ScopeGuard scope="users:read">
                        <UsersPage />
                      </ScopeGuard>
                    }
                  />
                  <Route
                    path="admins"
                    element={
                      <ScopeGuard scope="admins:read">
                        <AdminsPage />
                      </ScopeGuard>
                    }
                  />
                  <Route
                    path="audit-logs"
                    element={
                      <ScopeGuard scope="audit:read">
                        <AuditLogsPage />
                      </ScopeGuard>
                    }
                  />
                  <Route
                    path="impersonations"
                    element={
                      <ScopeGuard scope="impersonation:read">
                        <ImpersonationsPage />
                      </ScopeGuard>
                    }
                  />
                  <Route
                    path="policies"
                    element={
                      <ScopeGuard scope="policies:read">
                        <PoliciesPage />
                      </ScopeGuard>
                    }
                  />
                  <Route
                    path="policies/:policyId"
                    element={
                      <ScopeGuard scope="policies:read">
                        <PolicyDetailPage />
                      </ScopeGuard>
                    }
                  />
                  <Route
                    path="orders"
                    element={
                      <ScopeGuard scope="orders:read">
                        <OrdersPage />
                      </ScopeGuard>
                    }
                  />
                  <Route
                    path="orders/:orderId"
                    element={
                      <ScopeGuard scope="orders:read">
                        <OrderDetailPage />
                      </ScopeGuard>
                    }
                  />
                  <Route
                    path="roles"
                    element={
                      <ScopeGuard scope="roles:read">
                        <RolesPage />
                      </ScopeGuard>
                    }
                  />
                </Route>
              </Route>
              {/* — PUBLIC — */}
              <Route path={ROUTES.root} element={<PublicRoute element={<HomePage />} />} />
              <Route
                path={ROUTES.downloadApp}
                element={<PublicRoute element={<DownloadAppPage />} />}
              />
              <Route path={ROUTES.contacts} element={<PublicRoute element={<ContactsPage />} />} />
              <Route
                path={ROUTES.documents}
                element={<PublicRoute element={<DocumentsPage />} />}
              />
              <Route
                path={ROUTES.documentViewer}
                element={<PublicRoute element={<DocumentViewerPage />} />}
              />
              <Route path={ROUTES.info} element={<PublicRoute element={<InfoPage />} />} />

              <Route path={ROUTES.faq} element={<PublicRoute element={<FaqPage />} />} />

              {/* формы auth: доступны только НЕ-авторизованным */}
              <Route
                path={ROUTES.signin}
                element={
                  <ProtectedRoute
                    element={<SignInPage />}
                    redirectTo={ROUTES.myHealth.root}
                    accessLevel="unauthOnly"
                  />
                }
              />
              <Route
                path={ROUTES.forgotPassword}
                element={<PublicRoute element={<ForgotPasswordPage />} />}
              />
              <Route
                path={ROUTES.resetPassword}
                element={<PublicRoute element={<ResetPasswordPage />} />}
              />
              <Route
                path={ROUTES.signup}
                element={
                  <ProtectedRoute
                    element={<SignUpPage />}
                    redirectTo={ROUTES.myHealth.root}
                    accessLevel="unauthOnly"
                  />
                }
              />
              {/* — auth-only — */}
              <Route
                path={ROUTES.verifyEmail}
                element={
                  <ProtectedRoute
                    element={<VerifyEmailPage />}
                    redirectTo={ROUTES.signup}
                    accessLevel="authNotVerified"
                  />
                }
              />
              <Route
                path={ROUTES.checkup}
                element={
                  <ProtectedRoute
                    element={<CheckupStartPage />}
                    redirectTo={ROUTES.verifyEmail}
                    accessLevel="authVerifiedNotTest"
                  />
                  // <PublicRoute element={<CheckupStartPage />} />
                }
              />
              <Route
                path={ROUTES.questionnaire}
                element={
                  <ProtectedRoute
                    element={<QuestionnairePage />}
                    redirectTo={ROUTES.signup}
                    accessLevel="authVerifiedTestOrRerun"
                  />
                  // <PublicRoute element={<QuestionnairePage />} />
                }
              />
              {/* profile */}
              <Route
                path={`${ROUTES.profile.root}/*`}
                element={
                  <ProtectedRoute
                    element={<ProfilePage />}
                    redirectTo={ROUTES.signin}
                    accessLevel="authOnly"
                  />
                }
              />
              {/* Ментальное здоровье — отдельный флоу, без основного опросника */}
              <Route
                path={ROUTES.myHealth.mentalHealth}
                element={
                  isMentalHealthEnabled ? (
                    <ProtectedRoute
                      element={<MentalHealthLayout />}
                      redirectTo={ROUTES.signin}
                      accessLevel="authOnly"
                    />
                  ) : (
                    <Navigate to={ROUTES.root} replace />
                  )
                }
              />
              {/* — testPassedOnly — */}
              <Route
                path={`${ROUTES.myHealth.root}/*`}
                element={
                  <ProtectedRoute
                    element={<HealthPage />}
                    redirectTo={ROUTES.checkup}
                    accessLevel="testPassedOnly"
                  />
                }
              />
            </Routes>
          </SmartSuspense>
        </ErrorBoundary>
      </main>
      <ToastContainer
        position="top-right"
        newestOnTop
        hideProgressBar
        closeOnClick
        pauseOnHover
        draggable={false}
      />
      {!isDocumentViewerPage && !isAdminPage && <Footer />}
    </div>
  );
};

export default App;
